import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import styles from "./modalui.module.scss";
import { trackEvent, MODAL_EVENTS, type ModalEventProperties, enrichEventProperties } from "src/services/analytics";

interface ModaluiProps {
  open: boolean;
  handleClose?: (_event?: object, _reason?: string) => void;
  children: React.ReactNode;
  modaluiOver?: string;
  modalShadowOver?: string;
  backgroundColor?: string;
  disableBackdropClick?: boolean;
  modalType?: string;
  embedded?: boolean;
}

export default function Modalui({
  open,
  handleClose,
  children,
  modaluiOver,
  modalShadowOver,
  backgroundColor,
  disableBackdropClick = false,
  modalType = "unknown",
  embedded = false
}: ModaluiProps) {
  const previousOpenRef = useRef(open);

  useEffect(() => {
    if (open && !previousOpenRef.current) {
      // Modal just opened
      const properties = enrichEventProperties<ModalEventProperties>({
        modal_type: modalType
      });

      trackEvent(MODAL_EVENTS.MODAL_OPENED, properties);
    } else if (!open && previousOpenRef.current) {
      // Modal just closed
      const properties = enrichEventProperties<ModalEventProperties>({
        modal_type: modalType
      });

      trackEvent(MODAL_EVENTS.MODAL_CLOSED, properties);
    }
    previousOpenRef.current = open;
  }, [open, modalType]);

  const handleModalClose = (event: object, reason: string) => {
    if (disableBackdropClick && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }

    // Track backdrop click if applicable
    if (reason === "backdropClick") {
      const properties = enrichEventProperties<ModalEventProperties>({
        modal_type: modalType,
        close_method: "backdrop"
      });

      trackEvent(MODAL_EVENTS.MODAL_BACKDROP_CLICKED, properties);
    }

    if (handleClose) {
      handleClose(event, reason);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      className={`${styles.modalShadow} ${embedded ? styles.embedded : ""} ${modalShadowOver ?? ""}`}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: backgroundColor
        }
      }}
    >
      <Box className={`${styles.modalui} ${modaluiOver}`}>{children}</Box>
    </Modal>
  );
}
